import type {
  OrderVariant,
  PriceTier,
  Product,
  ProductFaq,
  ProductVariantPhotos,
  ProductVariantStock,
} from '@/types/products';

/**
 * Estado del formulario de un producto y su traducción al contrato del backend.
 *
 * El formulario trabaja con STRINGS (es lo que devuelve un `<input>`) y con filas que tienen un
 * `id` solo para la UI; el backend espera números y arrays de JSON. Igual que en Ajustes
 * (`settings-payload.ts`), esta capa es la única que conoce las dos formas:
 *
 *  - `productFormStateFrom`  → producto de la API a estado del formulario (tolerante con los
 *    JSON que lleguen con otra forma: los lee con `unknown` y descarta lo que no entiende);
 *  - `createProductPayload`  → body del `POST` (los campos opcionales vacíos se OMITEN);
 *  - `updateProductPayload`  → body del `PATCH` (los campos vacíos que la API sabe vaciar
 *    viajan como `null`; `inventario` vacío apaga el control de inventario).
 *
 * Las filas que el usuario deja en blanco se descartan al enviar, así que no hay que "limpiar"
 * el formulario a mano.
 */

export interface PriceTierRow {
  /** Identificador solo para la UI (`key` de React); no viaja al backend. */
  id: string;
  /** Cantidad mínima, como texto (`''` = sin escribir). */
  cantidad: string;
  /** Precio unitario, como texto (acepta `,` o `.` como separador decimal). */
  precio: string;
}

export interface OrderVariantForm {
  id: string;
  nombre: string;
  /** Opciones de la variante; `['']` = una fila vacía en la UI. */
  opciones: string[];
}

export interface FaqForm {
  id: string;
  pregunta: string;
  respuesta: string;
}

export interface ProductFormState {
  nombre: string;
  descripcion: string;
  /** Marca (ej. `Nike`); `''` = sin definir. Un valor por producto, no etiquetas. */
  marca: string;
  /** Categoría (ej. `Zapatillas`); `''` = sin definir. */
  categoria: string;
  /** Inventario como texto (`''` = sin control de inventario). */
  inventario: string;
  /**
   * Inventario POR COMBINACIÓN (21-sep-2026): clave canónica de la combinación (la misma que calcula
   * `claveCombinacion`, espejo del backend) → cantidad como texto (`''` = esa combinación queda sin
   * control y no se guarda).
   */
  inventarioVariantes: Record<string, string>;
  /**
   * Fotos POR VARIANTE (21-sep-2026): una fila por VALOR (ej. el color «Rojo») con SUS fotos, tal cual
   * las guarda el backend (`{ variantes: { Color: 'Rojo' }, fotos: [...] }`). Solo se guardan las filas
   * CON fotos: lo que no tenga fila usa las fotos generales de `fotos`.
   *
   * La fila fija UN eje (el color), así que sus fotos valen para todas las tallas de ese color: es el
   * mismo criterio que Shopify (la foto es de la variante «color», no de cada combinación).
   */
  fotosVariantes: ProductVariantPhotos[];
  activo: boolean;
  fotos: string[];
  tablaPrecios: PriceTierRow[];
  datosParaOrden: OrderVariantForm[];
  reglas: string[];
  faqs: FaqForm[];
  metaAdIds: string[];
}

/**
 * Campos del formulario a los que `formErrorsFromApiError` puede atribuir un mensaje del
 * backend (los `refine` sobre el objeto completo caen en el aviso general del formulario).
 */
export const PRODUCT_FORM_FIELDS = [
  'nombre',
  'descripcion',
  'marca',
  'categoria',
  'fotos',
  'tablaPrecios',
  'datosParaOrden',
  'reglas',
  'faqs',
  'inventario',
  'inventarioVariantes',
  'fotosVariantes',
  'activo',
  'metaAdIds',
] as const;

/* ---------------------------------------------------------------------------
 * Filas de la UI
 * ------------------------------------------------------------------------- */

let rowCounter = 0;

/** Id único por fila (para poder borrarlas sin que React reutilice el estado de otra). */
function nextRowId(prefix: string): string {
  rowCounter += 1;
  return `${prefix}-${rowCounter}`;
}

export function emptyPriceTierRow(cantidad = '', precio = ''): PriceTierRow {
  return { id: nextRowId('rango'), cantidad, precio };
}

export function emptyOrderVariant(nombre = '', opciones: string[] = []): OrderVariantForm {
  return {
    id: nextRowId('variante'),
    nombre,
    opciones: opciones.length > 0 ? opciones : [''],
  };
}

export function emptyFaq(pregunta = '', respuesta = ''): FaqForm {
  return { id: nextRowId('faq'), pregunta, respuesta };
}

/** Formulario de un producto nuevo (un rango de precio vacío, como exige el backend). */
export function emptyProductForm(): ProductFormState {
  return {
    nombre: '',
    descripcion: '',
    marca: '',
    categoria: '',
    inventario: '',
    inventarioVariantes: {},
    fotosVariantes: [],
    activo: true,
    fotos: [],
    tablaPrecios: [emptyPriceTierRow()],
    datosParaOrden: [],
    reglas: [],
    faqs: [],
    metaAdIds: [],
  };
}

/* ---------------------------------------------------------------------------
 * Lectura tolerante de los campos JSON
 * ------------------------------------------------------------------------- */

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Texto de un valor JSON (los números de la tabla de precios vienen como `number`). */
function textOf(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

/** Tabla de precios de la API → filas del formulario (siempre al menos una fila). */
export function priceTierRowsFromJson(value: unknown): PriceTierRow[] {
  const rows: PriceTierRow[] = [];

  if (Array.isArray(value)) {
    for (const raw of value) {
      const tier = asRecord(raw);
      if (!tier) continue;

      const cantidad = textOf(tier.cantidad);
      const precio = textOf(tier.precio);
      if (cantidad || precio) rows.push(emptyPriceTierRow(cantidad, precio));
    }
  }

  return rows.length > 0 ? rows : [emptyPriceTierRow()];
}

/** Variantes de la API → filas del formulario (el orden se conserva). */
export function orderVariantFormsFromJson(value: unknown): OrderVariantForm[] {
  if (!Array.isArray(value)) return [];

  const rows: OrderVariantForm[] = [];

  for (const raw of value) {
    const variant = asRecord(raw);
    if (!variant) continue;

    const nombre = textOf(variant.nombre);
    const opciones = Array.isArray(variant.opciones)
      ? variant.opciones.map(textOf).filter((opcion) => opcion.length > 0)
      : [];

    if (nombre || opciones.length > 0) rows.push(emptyOrderVariant(nombre, opciones));
  }

  return rows;
}

/** FAQs de la API → filas del formulario. */
export function faqFormsFromJson(value: unknown): FaqForm[] {
  if (!Array.isArray(value)) return [];

  const rows: FaqForm[] = [];

  for (const raw of value) {
    const faq = asRecord(raw);
    if (!faq) continue;

    const pregunta = textOf(faq.pregunta);
    const respuesta = textOf(faq.respuesta);
    if (pregunta || respuesta) rows.push(emptyFaq(pregunta, respuesta));
  }

  return rows;
}

/** Lista de strings de la API (`fotos`, `reglas`, `metaAdIds`). */
export function stringListFromJson(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map(textOf).filter((item) => item.length > 0);
}

/* ---------------------------------------------------------------------------
 * Inventario por combinación (21-sep-2026)
 * ------------------------------------------------------------------------- */

/** Tope de combinaciones que genera la pantalla (espejo de `MAX_COMBINACIONES` del backend). */
export const MAX_COMBINACIONES = 500;

/** Una combinación posible de las variantes cargadas en el formulario. */
export interface VarianteCombinacion {
  /** Clave canónica (espejo de `claveVariantes` del backend). */
  clave: string;
  /** Texto para la pantalla: "Hombre · 42". */
  etiqueta: string;
  /** Valores con el nombre de la variante como clave: `{ Talla: '42', Genero: 'Hombre' }`. */
  variantes: Record<string, string>;
}

/** minúsculas, sin acentos y sin espacios extra (espejo de `normalizar` del backend). */
function normalizarClave(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

/**
 * Clave canónica de una combinación: pares `clave=valor` normalizados, ordenados y unidos.
 * Es el ESPEJO EXACTO de `claveVariantes` (`backend/src/modules/products/inventory.ts`): así el mapa
 * de cantidades del formulario y la matriz guardada se reconocen sin depender del orden ni de las
 * mayúsculas.
 */
export function claveCombinacion(variantes: Record<string, string>): string {
  return Object.entries(variantes)
    .map(([clave, valor]) => [normalizarClave(clave), normalizarClave(valor)] as const)
    .filter(([clave, valor]) => clave.length > 0 && valor.length > 0)
    .map(([clave, valor]) => `${clave}=${valor}`)
    .sort()
    .join('|');
}

/**
 * Combinaciones posibles de las variantes que el bot pide (`datosParaOrden`): el producto cartesiano
 * de los ejes con NOMBRE y al menos una OPCIÓN. Se corta en `limite` (500, igual que el backend) para
 * que un producto con 4 ejes × 10 opciones no genere una tabla inmanejable: la pantalla avisa.
 */
export function combinacionesVariantes(
  rows: OrderVariantForm[],
  limite: number = MAX_COMBINACIONES,
): VarianteCombinacion[] {
  const ejes = rows
    .map((row) => ({
      nombre: row.nombre.trim(),
      opciones: [...new Set(row.opciones.map((opcion) => opcion.trim()).filter((opcion) => opcion.length > 0))],
    }))
    .filter((eje) => eje.nombre.length > 0 && eje.opciones.length > 0);

  if (ejes.length === 0) return [];

  let combinaciones: VarianteCombinacion[] = [{ clave: '', etiqueta: '', variantes: {} }];

  for (const eje of ejes) {
    const siguiente: VarianteCombinacion[] = [];

    for (const combinacion of combinaciones) {
      for (const opcion of eje.opciones) {
        if (siguiente.length >= limite) break;
        const variantes = { ...combinacion.variantes, [eje.nombre]: opcion };
        siguiente.push({
          clave: claveCombinacion(variantes),
          etiqueta: [...combinacion.etiqueta ? [combinacion.etiqueta] : [], opcion].filter(Boolean).join(' · '),
          variantes,
        });
      }
    }

    combinaciones = siguiente;
  }

  return combinaciones;
}

/**
 * Agrupa las combinaciones por el valor del PRIMER eje, para pintar la matriz como se lee de verdad:
 *
 *   Talla 42
 *     Dama     [ 5 ]
 *     Hombre   [ 2 ]
 *   Talla 43
 *     Dama     [ 0 ]
 *     Hombre   [ 1 ]
 *
 * Con un solo eje no hay grupos (una lista simple de valores). El orden de los ejes es el de
 * `datosParaOrden`, así que el primer eje es el que el bot pregunta primero.
 */
export interface GrupoCombinaciones {
  /** Título del grupo (ej. "Talla 42"); `''` cuando el producto tiene un solo eje. */
  titulo: string;
  /** Filas del grupo: clave canónica + lo que se muestra a la izquierda ("Dama"). */
  filas: Array<{ clave: string; etiqueta: string }>;
}

export function agruparCombinaciones(combos: VarianteCombinacion[]): GrupoCombinaciones[] {
  if (combos.length === 0) return [];

  const ejes = Object.keys(combos[0]?.variantes ?? {});
  const listar = (combinacion: VarianteCombinacion, valores: string[]) => ({
    clave: combinacion.clave,
    etiqueta: valores.join(' · '),
  });

  if (ejes.length < 2) {
    return [
      {
        titulo: '',
        filas: combos.map((combinacion) => listar(combinacion, Object.values(combinacion.variantes))),
      },
    ];
  }

  const [primerEje, ...resto] = ejes;
  const grupos = new Map<string, GrupoCombinaciones>();

  for (const combinacion of combos) {
    const titulo = `${primerEje} ${combinacion.variantes[primerEje] ?? ''}`.trim();
    const grupo = grupos.get(titulo) ?? { titulo, filas: [] };

    grupo.filas.push(
      listar(
        combinacion,
        resto.map((eje) => combinacion.variantes[eje] ?? '').filter((valor) => valor.length > 0),
      ),
    );
    grupos.set(titulo, grupo);
  }

  return [...grupos.values()];
}

/**
 * ¿El producto tiene stock POR COMBINACIÓN? Es el que depende del CRUCE de dos o más ejes (talla 42 ·
 * negro). En el formulario se reconoce porque la clave canónica une más de un par (`a=b|c=d`).
 *
 * Cuando es `false` el stock es **por variante suelta** (una cantidad por opción, que es lo normal: cada
 * talla su número) y el panel no muestra la matriz de combinaciones.
 */
export function hayStockPorCombinacion(values: Record<string, string>): boolean {
  return Object.entries(values).some(
    ([clave, cantidad]) => clave.includes('|') && (cantidad ?? '').trim().length > 0,
  );
}

/** `inventarioVariantes` de la API → mapa `clave → cantidad` (texto) del formulario. */
export function inventarioVariantesFormFromJson(value: unknown): Record<string, string> {
  const mapa: Record<string, string> = {};
  if (!Array.isArray(value)) return mapa;

  for (const cruda of value) {
    const fila = asRecord(cruda);
    if (!fila) continue;

    const crudas = asRecord(fila.variantes);
    if (!crudas) continue;

    const variantes: Record<string, string> = {};
    for (const [clave, valor] of Object.entries(crudas)) {
      const texto = textOf(valor);
      if (clave.trim() && texto) variantes[clave] = texto;
    }
    if (Object.keys(variantes).length === 0) continue;

    const cantidad = textOf(fila.cantidad);
    if (cantidad) mapa[claveCombinacion(variantes)] = cantidad;
  }

  return mapa;
}

/** `fotosVariantes` de la API → filas del formulario (una por valor de variante, con sus fotos). */
export function fotosVariantesFormFromJson(value: unknown): ProductVariantPhotos[] {
  if (!Array.isArray(value)) return [];

  const filas: ProductVariantPhotos[] = [];

  for (const cruda of value) {
    const fila = asRecord(cruda);
    if (!fila) continue;

    const crudas = asRecord(fila.variantes);
    if (!crudas) continue;

    const variantes: Record<string, string> = {};
    for (const [clave, valor] of Object.entries(crudas)) {
      const texto = textOf(valor);
      if (clave.trim() && texto) variantes[clave] = texto;
    }
    if (Object.keys(variantes).length === 0) continue;

    const fotos = stringListFromJson(fila.fotos);
    if (fotos.length === 0) continue;

    filas.push({ variantes, fotos });
  }

  return filas;
}

/**
 * Mapa del formulario → `inventarioVariantes` del backend.
 *
 * Guarda DOS cosas, que conviven en el mismo mapa:
 *  1. **por variante suelta** (una cantidad por opción: talla 40 → 5), que es lo que el panel pide al
 *     lado de cada opción — la clave es de UN eje;
 *  2. **por combinación** (el cruce talla 42 · negro), que es más específico y lo carga la matriz — la
 *     clave une dos o más ejes.
 *
 * `null` = no queda ninguna cantidad (el producto vuelve a venderse sin control por variante). Las
 * claves se resuelven contra las variantes ACTUALES, así que una clave vieja que ya no existe no viaja.
 *
 * Esto es importante con dos o más ejes: antes solo se guardaban las claves de los CRUCES
 * (`combinacionesVariantes`), así que las cantidades por variante del panel se perdían al guardar.
 */
export function inventarioVariantesFromForm(
  form: ProductFormState,
): ProductVariantStock[] | null {
  const filas: ProductVariantStock[] = [];
  const usadas = new Set<string>();

  const agregar = (clave: string, variantes: Record<string, string>) => {
    const cantidad = parseIntegerField(form.inventarioVariantes?.[clave] ?? '');
    if (cantidad === null || usadas.has(clave)) return;

    usadas.add(clave);
    filas.push({ variantes, cantidad });
  };

  // 1) Por variante suelta: cada opción de cada eje tiene su propia cantidad.
  for (const eje of ejesConOpciones(form.datosParaOrden)) {
    for (const opcion of eje.opciones) {
      agregar(claveCombinacion({ [eje.nombre]: opcion }), { [eje.nombre]: opcion });
    }
  }

  // 2) Por combinación: solo los cruces (dos o más ejes), que son los que carga la matriz.
  for (const combinacion of combinacionesVariantes(form.datosParaOrden)) {
    if (Object.keys(combinacion.variantes).length < 2) continue;
    agregar(combinacion.clave, combinacion.variantes);
  }

  return filas.length > 0 ? filas : null;
}

/**
 * Mapa sin las claves indicadas: sirve para **vaciar una parte** del inventario (las combinaciones de la
 * matriz) sin tocar las cantidades que el panel pide al lado de cada variante. Devuelve un mapa NUEVO.
 */
export function sinClaves(
  values: Record<string, string>,
  claves: Iterable<string>,
): Record<string, string> {
  const quitar = new Set(claves);
  const siguiente: Record<string, string> = {};

  for (const [clave, cantidad] of Object.entries(values)) {
    if (!quitar.has(clave)) siguiente[clave] = cantidad;
  }

  return siguiente;
}

/**
 * Filas del formulario → `fotosVariantes` del backend: lo que se manda es exactamente lo que el panel
 * muestra (una fila por valor de variante con sus fotos). `null` = no queda ninguna, así el producto
 * vuelve a usar solo las fotos generales de `fotos`.
 */
export function fotosVariantesFromForm(form: ProductFormState): ProductVariantPhotos[] | null {
  const filas: ProductVariantPhotos[] = [];

  for (const fila of form.fotosVariantes ?? []) {
    const fotos = cleanStringList(fila.fotos);
    if (fotos.length === 0) continue;
    filas.push({ variantes: fila.variantes, fotos });
  }

  return filas.length > 0 ? filas : null;
}

/* ---------------------------------------------------------------------------
 * Fotos por variante (21-sep-2026)
 * ------------------------------------------------------------------------- */

/** Palabras con las que se reconoce el eje «que cambia entre una foto y otra» (el color, como Shopify). */
const PALABRA_COLOR = /color|colour|tono|acabado/;

/** Palabras de un eje de TALLAS: dos ejes de este tipo no se cruzan (un zapato es caballero O dama). */
const PALABRA_TALLA = /talla|tamano|numero|size|calzado|pie/;

/** Un eje de variante (de `datosParaOrden`) con sus opciones cargadas. */
export interface EjeConOpciones {
  nombre: string;
  opciones: string[];
}

/**
 * Ejes de `datosParaOrden` con NOMBRE y al menos una OPCIÓN: son los únicos sobre los que se pueden
 * colgar fotos (una fila por opción, ej. una por color). Un eje sin opciones no da nada que elegir.
 */
export function ejesConOpciones(rows: OrderVariantForm[]): EjeConOpciones[] {
  return rows
    .map((row) => ({
      nombre: row.nombre.trim(),
      opciones: [
        ...new Set(row.opciones.map((opcion) => opcion.trim()).filter((opcion) => opcion.length > 0)),
      ],
    }))
    .filter((eje) => eje.nombre.length > 0 && eje.opciones.length > 0);
}

/**
 * Ejes sobre los que SE PUEDEN colgar fotos: los que tienen opciones **menos los de TALLA**
 * (24-sep-2026). Una foto no cambia entre la 40 y la 42 —el bot manda la misma—, así que la talla ni se
 * ofrece ni se elige: si el producto solo tiene tallas, esto devuelve `[]` y la sección lo explica en vez
 * de pintar una fila por talla (era lo que pasaba: sin un eje «Color», el respaldo caía en el primero
 * con opciones, que suele ser «Talla»).
 */
export function ejesParaFotos(rows: OrderVariantForm[]): EjeConOpciones[] {
  return ejesConOpciones(rows).filter((eje) => !PALABRA_TALLA.test(normalizarClave(eje.nombre)));
}

/**
 * Eje sobre el que se cuelgan las fotos: el **color** manda (es lo que cambia entre una foto y otra,
 * igual que en Shopify) y, si ningún eje se llama así, el primero que **no sea de talla** (por si un
 * negocio quiere fotos por otra variante, ej. «Estilo»). `''` = no hay ninguno: el producto solo tiene
 * tallas (o no tiene variantes con opciones) y la sección solo puede explicarlo.
 */
export function elegirEjeFotos(rows: OrderVariantForm[]): string {
  const ejes = ejesParaFotos(rows);
  const color = ejes.find((eje) => PALABRA_COLOR.test(normalizarClave(eje.nombre)));

  return (color ?? ejes[0])?.nombre ?? '';
}

/**
 * ¿TODAS las variantes son de tallas («Talla Caballero» y «Talla Dama»)? Dos ejes de talla **no se
 * cruzan** —un zapato es de caballero O de dama, nunca de los dos—, así que el cruce no existe y no
 * tiene sentido invitar a repartir el stock por combinación: ahí el stock se lleva por valor, al lado de
 * cada opción. El panel usa esto para no ofrecer la matriz en un producto así.
 */
export function todosLosEjesSonDeTalla(rows: OrderVariantForm[]): boolean {
  const ejes = ejesConOpciones(rows);

  return ejes.length > 1 && ejes.every((eje) => PALABRA_TALLA.test(normalizarClave(eje.nombre)));
}

/**
 * Índice de la fila de fotos de una opción de un eje (ej. las fotos del color «Rojo»), o `-1` si esa
 * opción todavía no tiene fila.
 *
 * Solo cuenta las filas que fijan ESE eje y SOLO ese: una combinación guardada por otra vía (color +
 * talla) no se mezcla con la fila del color, para que el panel no edite cosas que no muestra.
 */
export function filaFotosDeOpcion(rows: ProductVariantPhotos[], eje: string, opcion: string): number {
  const buscado = normalizarClave(eje);
  const valor = normalizarClave(opcion);

  return rows.findIndex((fila) => {
    const pares = Object.entries(fila.variantes);
    if (pares.length !== 1) return false;

    const [clave, suyo] = pares[0] as [string, string];
    return normalizarClave(clave) === buscado && normalizarClave(suyo) === valor;
  });
}

/**
 * ¿La fila pertenece al eje que se está editando? Es del eje cuando lo fija **y solo a él** (una fila
 * de color + talla es de una combinación vieja: el panel no la edita).
 */
export function filaEsDelEje(fila: ProductVariantPhotos, eje: string): boolean {
  const pares = Object.entries(fila.variantes);
  if (pares.length !== 1) return false;

  const [clave] = pares[0] as [string, string];
  return normalizarClave(clave) === normalizarClave(eje);
}

/**
 * Cuántas filas de fotos quedan FUERA del eje que el panel está editando (una combinación guardada por
 * otra vía, o las fotos de otro eje): se conservan tal cual al guardar, pero no se pintan.
 */
export function filasFueraDelEje(rows: ProductVariantPhotos[], eje: string): number {
  return rows.filter((fila) => !filaEsDelEje(fila, eje)).length;
}

/**
 * Agrega fotos a la fila de esa opción del eje (crea la fila si todavía no existía). La clave de
 * `variantes` es el nombre del eje tal como lo escribió el negocio, que es lo que guarda el backend.
 */
export function agregarFotosDeOpcion(
  rows: ProductVariantPhotos[],
  eje: string,
  opcion: string,
  fotos: string[],
): ProductVariantPhotos[] {
  if (fotos.length === 0) return rows;

  const indice = filaFotosDeOpcion(rows, eje, opcion);
  if (indice < 0) return [...rows, { variantes: { [eje]: opcion }, fotos }];

  return rows.map((fila, position) =>
    position === indice ? { ...fila, fotos: [...fila.fotos, ...fotos] } : fila,
  );
}

/** Quita UNA foto de la fila de esa opción; si la fila se queda sin fotos, desaparece. */
export function quitarFotoDeOpcion(
  rows: ProductVariantPhotos[],
  eje: string,
  opcion: string,
  indiceFoto: number,
): ProductVariantPhotos[] {
  const indice = filaFotosDeOpcion(rows, eje, opcion);
  if (indice < 0) return rows;

  const fotos = (rows[indice]?.fotos ?? []).filter((_foto, position) => position !== indiceFoto);
  if (fotos.length === 0) return rows.filter((_fila, position) => position !== indice);

  return rows.map((fila, position) => (position === indice ? { ...fila, fotos } : fila));
}

/** Quita la fila COMPLETA de esa opción (ese color vuelve a usar las fotos generales). */
export function quitarFilaDeOpcion(
  rows: ProductVariantPhotos[],
  eje: string,
  opcion: string,
): ProductVariantPhotos[] {
  const indice = filaFotosDeOpcion(rows, eje, opcion);

  return indice < 0 ? rows : rows.filter((_fila, position) => position !== indice);
}

/** Producto de la API → estado del formulario. `null` = producto nuevo. */
export function productFormStateFrom(product: Product | null): ProductFormState {
  if (!product) return emptyProductForm();

  return {
    nombre: product.nombre,
    descripcion: product.descripcion ?? '',
    marca: product.marca ?? '',
    categoria: product.categoria ?? '',
    inventario: product.inventario === null ? '' : String(product.inventario),
    inventarioVariantes: inventarioVariantesFormFromJson(product.inventarioVariantes),
    fotosVariantes: fotosVariantesFormFromJson(product.fotosVariantes),
    activo: product.activo,
    fotos: stringListFromJson(product.fotos),
    tablaPrecios: priceTierRowsFromJson(product.tablaPrecios),
    datosParaOrden: orderVariantFormsFromJson(product.datosParaOrden),
    reglas: stringListFromJson(product.reglas),
    faqs: faqFormsFromJson(product.faqs),
    metaAdIds: stringListFromJson(product.metaAdIds),
  };
}

/* ---------------------------------------------------------------------------
 * Conversión de vuelta (formulario → backend)
 * ------------------------------------------------------------------------- */

/** Entero de un input de texto (`''` o algo que no es un entero → `null`). */
export function parseIntegerField(value: string): number | null {
  const text = value.trim();
  if (!/^-?\d+$/.test(text)) return null;
  return Number(text);
}

/** Precio de un input de texto: acepta `25000`, `25000.5` y `25000,5`. */
export function parsePriceField(value: string): number | null {
  const text = value.trim().replace(',', '.');
  if (!/^-?\d+(\.\d+)?$/.test(text)) return null;

  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
}

/** ¿La fila está totalmente en blanco? (se ignora al enviar). */
export function isPriceTierRowEmpty(row: PriceTierRow): boolean {
  return row.cantidad.trim().length === 0 && row.precio.trim().length === 0;
}

/** Textos de la UI → `string[]` del backend, sin espacios sobrantes ni filas vacías. */
export function cleanStringList(values: string[]): string[] {
  return values.map((value) => value.trim()).filter((value) => value.length > 0);
}

/**
 * Ordena la tabla de precios por cantidad ascendente.
 *
 * El backend guarda el JSON tal cual, pero `serializeCatalog` lo manda al prompt del bot: si el
 * usuario escribe los rangos desordenados, el bot los leería desordenados. Se normaliza aquí
 * para que el mismo catálogo produzca siempre el mismo prompt.
 */
export function sortPriceTiers(tiers: PriceTier[]): PriceTier[] {
  return [...tiers].sort((a, b) => a.cantidad - b.cantidad);
}

/** Filas del formulario → `tablaPrecios` del backend (filas vacías descartadas). */
export function priceTiersFromRows(rows: PriceTierRow[]): PriceTier[] {
  const tiers: PriceTier[] = [];

  for (const row of rows) {
    if (isPriceTierRowEmpty(row)) continue;

    const cantidad = parseIntegerField(row.cantidad);
    const precio = parsePriceField(row.precio);
    if (cantidad === null || precio === null) continue;

    tiers.push({ cantidad, precio });
  }

  return sortPriceTiers(tiers);
}

/** Filas → `datosParaOrden` (el ORDEN se respeta: es el orden en que el bot pregunta). */
export function orderVariantsFromRows(rows: OrderVariantForm[]): OrderVariant[] {
  const variantes: OrderVariant[] = [];

  for (const row of rows) {
    const nombre = row.nombre.trim();
    const opciones = cleanStringList(row.opciones);

    if (!nombre && opciones.length === 0) continue;
    variantes.push(opciones.length > 0 ? { nombre, opciones } : { nombre });
  }

  return variantes;
}

/** Filas → `faqs`. */
export function faqsFromRows(rows: FaqForm[]): ProductFaq[] {
  const faqs: ProductFaq[] = [];

  for (const row of rows) {
    const pregunta = row.pregunta.trim();
    const respuesta = row.respuesta.trim();

    if (!pregunta && !respuesta) continue;
    faqs.push({ pregunta, respuesta });
  }

  return faqs;
}

/**
 * Body del `POST`. Los campos opcionales que quedaron vacíos NO viajan: el backend aplica sus
 * valores por defecto (`fotos: []`, `metaAdIds: []`, `inventario: null`, `activo: true`).
 */
export function createProductPayload(form: ProductFormState): {
  nombre: string;
  tablaPrecios: PriceTier[];
  activo: boolean;
  descripcion?: string;
  marca?: string;
  categoria?: string;
  fotos?: string[];
  datosParaOrden?: OrderVariant[];
  reglas?: string[];
  faqs?: ProductFaq[];
  inventario?: number;
  inventarioVariantes?: ProductVariantStock[];
  fotosVariantes?: ProductVariantPhotos[];
  metaAdIds?: string[];
} {
  const descripcion = form.descripcion.trim();
  const marca = form.marca.trim();
  const categoria = form.categoria.trim();
  const fotos = cleanStringList(form.fotos);
  const variantes = orderVariantsFromRows(form.datosParaOrden);
  const reglas = cleanStringList(form.reglas);
  const faqs = faqsFromRows(form.faqs);
  const metaAdIds = cleanStringList(form.metaAdIds);
  const inventario = parseIntegerField(form.inventario);
  const inventarioVariantes = inventarioVariantesFromForm(form);
  const fotosVariantes = fotosVariantesFromForm(form);

  return {
    nombre: form.nombre.trim(),
    tablaPrecios: priceTiersFromRows(form.tablaPrecios),
    activo: form.activo,
    ...(descripcion.length > 0 ? { descripcion } : {}),
    ...(marca.length > 0 ? { marca } : {}),
    ...(categoria.length > 0 ? { categoria } : {}),
    ...(fotos.length > 0 ? { fotos } : {}),
    ...(variantes.length > 0 ? { datosParaOrden: variantes } : {}),
    ...(reglas.length > 0 ? { reglas } : {}),
    ...(faqs.length > 0 ? { faqs } : {}),
    ...(inventario !== null ? { inventario } : {}),
    ...(inventarioVariantes !== null ? { inventarioVariantes } : {}),
    ...(fotosVariantes !== null ? { fotosVariantes } : {}),
    ...(metaAdIds.length > 0 ? { metaAdIds } : {}),
  };
}

/**
 * Body del `PATCH`. Aquí SÍ viaja todo, porque el backend distingue `undefined` ("no toques
 * esto") de `null` ("vacía esto"): lo que quedó vacío se manda como `null` en los campos que el
 * contrato acepta así (`descripcion`, `datosParaOrden`, `reglas`, `faqs`, `inventario`,
 * `inventarioVariantes` y `fotosVariantes`).
 *
 * `fotos`, `metaAdIds` y `tablaPrecios` no tienen forma de vaciarse en el contrato (el schema
 * exige al menos un rango de precio y una lista vacía de fotos se puede mandar como `[]`), así
 * que se mandan tal como quedaron: si el usuario borró todos los rangos, el backend responde 400
 * con el motivo en vez de guardar un producto sin precio.
 */
export function updateProductPayload(form: ProductFormState): {
  nombre: string;
  descripcion: string | null;
  marca: string | null;
  categoria: string | null;
  fotos: string[];
  tablaPrecios: PriceTier[];
  datosParaOrden: OrderVariant[] | null;
  reglas: string[] | null;
  faqs: ProductFaq[] | null;
  inventario: number | null;
  inventarioVariantes: ProductVariantStock[] | null;
  fotosVariantes: ProductVariantPhotos[] | null;
  activo: boolean;
  metaAdIds: string[];
} {
  const descripcion = form.descripcion.trim();
  const marca = form.marca.trim();
  const categoria = form.categoria.trim();
  const variantes = orderVariantsFromRows(form.datosParaOrden);
  const reglas = cleanStringList(form.reglas);
  const faqs = faqsFromRows(form.faqs);

  return {
    nombre: form.nombre.trim(),
    descripcion: descripcion.length > 0 ? descripcion : null,
    marca: marca.length > 0 ? marca : null,
    categoria: categoria.length > 0 ? categoria : null,
    fotos: cleanStringList(form.fotos),
    tablaPrecios: priceTiersFromRows(form.tablaPrecios),
    datosParaOrden: variantes.length > 0 ? variantes : null,
    reglas: reglas.length > 0 ? reglas : null,
    faqs: faqs.length > 0 ? faqs : null,
    inventario: parseIntegerField(form.inventario),
    inventarioVariantes: inventarioVariantesFromForm(form),
    fotosVariantes: fotosVariantesFromForm(form),
    activo: form.activo,
    metaAdIds: cleanStringList(form.metaAdIds),
  };
}
